/* ────────────────────────────────────────────────────────────────────────────
   Sticky-hover gate  (fixes the "random stuck dark row" on the tables)

   Browsers drive :hover purely off the pointer, and Chromium only re-evaluates
   which element is hovered on the next *real* pointer event. So whenever the
   content under a STATIONARY cursor shifts — the row that was hovered keeps its
   :hover tint even though the cursor is no longer over it. It reads as a random
   dark/highlighted row that, until now, only a hard page refresh cleared.

   The things that shift content under a parked cursor (none of which fire a
   pointer event, so none of which let the browser fix :hover on its own):
     • scrolling a long list (mouse wheel / trackpad / keyboard) — the #1 cause
     • a silent in-session reload that reorders/inserts/removes rows
       (inline-edit refetch, 5-min auto-refresh, live-refresh bus, month poll)
     • an overlay (modal / drawer / dropdown) opening or closing over a row
     • alt-tabbing or switching browser tabs and coming back
     • a hard refresh with the pointer already parked on a row

   Fix — gate every row-hover CSS rule behind `html.pointer-active`:

     @media (hover: hover) {
       html.pointer-active <row>:hover > td { background: <tint>; }
     }

   and toggle that class from here:
     • turn it ON  on genuine pointer movement (pointermove / pointerdown) —
       at that instant the browser has just re-hit-tested, so :hover is fresh.
     • turn it OFF on every event above. While the class is off the rule can't
       match, so any stale tint repaints to the row's base colour immediately;
       the next real pointer move turns it back on, re-binding the tint to the
       actual row under the cursor.

   Net effect: hover looks and behaves exactly as before whenever the mouse is
   moving or resting after a move — it simply can never get "stuck".
   ──────────────────────────────────────────────────────────────────────────── */

let installed = false;

/** Drop the hover gate so any stale :hover tint repaints to the row's base
 *  colour right away. Exposed so a data-driven reload (see DataTable) can clear
 *  a tint left on a row that shifted under a stationary cursor, without waiting
 *  for the user's next scroll. The next real pointer move re-arms it. */
export function suppressStickyHover(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.remove('pointer-active');
}

/** Install the global listeners exactly once. Idempotent. */
export function installHoverGate(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const enable = () => document.documentElement.classList.add('pointer-active');
  const disable = suppressStickyHover;

  // Real pointer activity ⇒ the browser just re-hit-tested ⇒ :hover is fresh.
  window.addEventListener('pointermove', enable, { passive: true });
  window.addEventListener('pointerdown', enable, { passive: true });

  // Content shifted under the cursor / cursor left the page ⇒ :hover is stale.
  // `capture: true` on scroll is essential — scroll events don't bubble, so we
  // must catch them on the way DOWN to see scrolling inside any inner container
  // (the page card, a table's own scroll wrapper), not just the window.
  window.addEventListener('scroll', disable, { passive: true, capture: true });
  window.addEventListener('wheel', disable, { passive: true });
  window.addEventListener('blur', disable);
  document.addEventListener('visibilitychange', disable);
  // Pointer leaving the viewport entirely (off to another monitor / devtools).
  document.documentElement.addEventListener('pointerleave', disable, { passive: true });
}

// Arm on import. The gate starts OFF (class absent), so a hard refresh with the
// cursor parked on a row never paints — and therefore never "sticks" — a tint;
// the user's first genuine pointer move switches it on.
installHoverGate();
